import { useRouteError } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { useEffect, useState } from 'react';
import AppNavigation from './AppNavigation';
import SidebarVisibilityProvider from '../../contexts/SidebarVisibilityContext';
import { SignInModalProvider } from '../../contexts/SignInModalContext';
import { NAVBAR_MOBILE_HEIGHT } from '../../utils/layout';
import { useBreakpoints } from '../../hooks/useBreakpoints';

export default function ErrorPage() {
  const [isHandlingError, setIsHandlingError] = useState(true);
  const error = useRouteError();
  console.error(error);

  const { breakpoint } = useBreakpoints();
  const isMobile = ['xs', 'sm'].includes(breakpoint);

  useEffect(() => {
    // Function to determine if the error is related to dynamic imports
    const isDynamicImportError = () => {
      return error?.message?.includes('Failed to fetch dynamically imported module');
    };

    if (isDynamicImportError()) {
      const hasReloaded = sessionStorage.getItem('hasReloaded');

      if (!hasReloaded) {
        console.log('Reloading to clear dynamically imported module error');
        sessionStorage.setItem('hasReloaded', 'true');
        window.location.reload();
        return;
      } else {
        // Reload already attempted — report to Sentry and show error UI
        sessionStorage.removeItem('hasReloaded');
        captureException(error, { level: 'fatal' });
      }
    } else {
      captureException(error, { level: 'fatal' });
    }

    setIsHandlingError(false);
  }, [error]);

  // The router renders this in place of Base, so it has to supply the providers
  // AppNavigation needs itself: a signed-out sidebar calls useSignInModal().
  return (
    <SidebarVisibilityProvider>
      <SignInModalProvider>
        <div
          className='flex h-screen'
          style={{
            '--top-nav-height': isMobile ? NAVBAR_MOBILE_HEIGHT : '0px',
            marginTop: isMobile ? NAVBAR_MOBILE_HEIGHT : 0,
          }}>
          <AppNavigation />
          <div className='flex-1 flex flex-col min-w-0 h-screen'>
            {!isHandlingError && (
              <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='mx-auto max-w-4xl'>
                  <div id='error-page' className='my-8 grid gap-2'>
                    <h1 className='text-lg text-emerald-900 font-bold'>Oops!</h1>
                    <p>Sorry, an unexpected error has occurred.</p>
                    <p>
                      <i>{error.statusText || error.message}</i>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SignInModalProvider>
    </SidebarVisibilityProvider>
  );
}
